import {
  WhmBox,
  WhmButton,
  WhmLinearProgress,
  whmTheme,
  WhmTypography,
  useMediaQuery,
} from "@securitize/reactjs-whm";
import {
  KybSectionStatus,
  KybSectionStatuses,
  RequestedUpdatesGrouped,
} from "../../../data/dtos/onboarding-status.dto";
import { Translate } from "../../Translation/Translate";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { useUnresolvedSectionCounts } from "./useUnresolvedSectionCounts";

function isSectionReady(status?: KybSectionStatus) {
  return (
    status === KybSectionStatuses.COMPLETED ||
    status === KybSectionStatuses.UPDATES_REQUIRED
  );
}

function isSectionFullyResolved(
  status: KybSectionStatus | undefined,
  unresolvedCount: number,
) {
  if (status === KybSectionStatuses.COMPLETED) {
    return true;
  }
  if (status === KybSectionStatuses.UPDATES_REQUIRED) {
    return unresolvedCount === 0;
  }
  return false;
}

export default function EntityOnboardingHeader({
  entityInformationStatus,
  keyPartiesStatus,
  documentsStatus,
  requestedUpdatesGrouped,
  setShowSuccessModal,
  isUpdatesRequired,
  compressed = false,
}: {
  entityInformationStatus?: KybSectionStatus;
  keyPartiesStatus?: KybSectionStatus;
  documentsStatus?: KybSectionStatus;
  requestedUpdatesGrouped?: RequestedUpdatesGrouped;
  setShowSuccessModal: (show: boolean) => void;
  isUpdatesRequired: boolean;
  compressed?: boolean;
}) {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("md"));

  const {
    unresolvedEntityInformationCount,
    unresolvedDocumentsCount,
    unresolvedKeyPartiesCount,
  } = useUnresolvedSectionCounts(requestedUpdatesGrouped);

  const isSubmitReady =
    isSectionFullyResolved(
      entityInformationStatus,
      unresolvedEntityInformationCount,
    ) &&
    isSectionFullyResolved(keyPartiesStatus, unresolvedKeyPartiesCount) &&
    isSectionFullyResolved(documentsStatus, unresolvedDocumentsCount);

  const sections = [entityInformationStatus, keyPartiesStatus, documentsStatus];
  const completedCount = sections.filter(isSectionReady).length;
  const totalCount = sections.length;
  const progressText = `${completedCount} / ${totalCount}`;
  const progress = (completedCount / totalCount) * 100;

  const submitButton = (
    <WhmButton
      variant="contained"
      size="large"
      disabled={!isSubmitReady}
      onClick={() => setShowSuccessModal(true)}
    >
      <Translate
        translationKey={translationKeys.ONBOARDING_CARD_SUBMIT_BUTTON}
      />
    </WhmButton>
  );

  // Mobile layout: button above progress bar (left-aligned)
  if (isMobile) {
    return (
      <WhmBox
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingTop: compressed ? "8px" : "0",
          gap: "16px",
        }}
      >
        <WhmBox>
          <WhmTypography variant="h4">
            {isUpdatesRequired ? (
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_TITLE_UPDATES_REQUIRED
                }
              />
            ) : (
              <Translate
                translationKey={translationKeys.ONBOARDING_CARD_TITLE}
              />
            )}
          </WhmTypography>
          <WhmTypography
            variant="body2"
            color={whmTheme.palette.text.secondary}
          >
            {isUpdatesRequired ? (
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_SUBTITLE_UPDATES_REQUIRED
                }
              />
            ) : (
              <Translate
                translationKey={translationKeys.ONBOARDING_CARD_SUBTITLE}
              />
            )}
          </WhmTypography>
        </WhmBox>
        <WhmBox sx={{ display: "flex", justifyContent: "flex-start" }}>
          {submitButton}
        </WhmBox>
        {!isUpdatesRequired && (
          <WhmBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <WhmBox sx={{ flex: 1 }}>
              <WhmLinearProgress variant="determinate" value={progress} />
            </WhmBox>
            <WhmBox sx={{ width: "35px" }}>
              <WhmTypography
                variant="body2"
                color={whmTheme.palette.text.secondary}
                sx={{ textAlign: "right" }}
              >
                {progressText}
              </WhmTypography>
            </WhmBox>
          </WhmBox>
        )}
      </WhmBox>
    );
  }

  // Desktop/Tablet layout: button to the right
  return (
    <WhmBox
      sx={{
        display: "flex",
        flexDirection: "column",
        paddingTop: compressed ? "8px" : "0",
        gap: "16px",
      }}
    >
      <WhmBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <WhmBox
          sx={{
            flex: 1,
            paddingRight: compressed ? "0" : "16px",
          }}
        >
          <WhmTypography variant="h4">
            {isUpdatesRequired ? (
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_TITLE_UPDATES_REQUIRED
                }
              />
            ) : (
              <Translate
                translationKey={translationKeys.ONBOARDING_CARD_TITLE}
              />
            )}
          </WhmTypography>
          <WhmTypography
            variant="body2"
            color={whmTheme.palette.text.secondary}
          >
            {isUpdatesRequired ? (
              <Translate
                translationKey={
                  translationKeys.ONBOARDING_CARD_SUBTITLE_UPDATES_REQUIRED
                }
              />
            ) : (
              <Translate
                translationKey={translationKeys.ONBOARDING_CARD_SUBTITLE}
              />
            )}
          </WhmTypography>
        </WhmBox>
        <WhmBox
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {submitButton}
        </WhmBox>
      </WhmBox>
      {!isUpdatesRequired && (
        <WhmBox
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "420px",
            gap: "8px",
          }}
        >
          <WhmBox sx={{ flex: 1 }}>
            <WhmLinearProgress variant="determinate" value={progress} />
          </WhmBox>
          <WhmBox sx={{ width: "35px" }}>
            <WhmTypography
              variant="body2"
              color={whmTheme.palette.text.secondary}
              sx={{ textAlign: "right" }}
            >
              {progressText}
            </WhmTypography>
          </WhmBox>
        </WhmBox>
      )}
    </WhmBox>
  );
}
